import { Injectable } from '@angular/core';

import { Observable, of, Subject, throwError } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

interface CacheContent {
  expiry: number;
  value: any;
}

/**
 * refer: https://hackernoon.com/angular-simple-in-memory-cache-service-on-the-ui-with-rxjs-77f167387e39
 */
@Injectable()
export class CacheService {
  readonly DEFAULT_MAX_AGE: number = 300 * 1000;
  private readonly cache: Map<string, CacheContent> = new Map<string, CacheContent>();
  private readonly inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();

  clearCache() {
    this.cache.clear();
  }

  /**
   * Gets the value from cache if the key is provided.
   * If no value exists in cache, then check if the same call exists in flight.
   * If so return the subject.
   * If not create a new Subject inFlightObservable and return the source observable.
   */
  get<T>(key: string, fallback?: Observable<T>, maxAge: number = this.DEFAULT_MAX_AGE): Observable<T> {
    if (this.hasValidCachedValue(key)) {
      return of(this.cache.get(key).value);
    }

    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key);
    }

    if (fallback) {
      this.inFlightObservables.set(key, new Subject());

      return fallback.pipe(
        tap((value) => this.set(key, value, maxAge)),
        finalize(() => this.inFlightObservables.delete(key)),
      );
    }

    return throwError('Requested key is not available in Cache');
  }

  // getCacheKeys(): string[] {
  //   return [...this.cache.keys()];
  // }

  setInFlightObservables(key: string) {
    this.inFlightObservables.set(key, new Subject());
  }

  /**
   * Sets the value with key in the cache
   * Notifies all observers of the new value
   */
  set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
    this.cache.set(key, { value, expiry: Date.now() + maxAge });
    this.notifyInFlightObservers(key, value);
  }

  /**
   * Checks if the a key exists in cache
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Checks if the key exists and has not expired.
   */
  private hasValidCachedValue(key: string): boolean {
    if (!this.cache.has(key)) {
      return false;
    }

    if (this.cache.get(key).expiry < Date.now()) {
      this.cache.delete(key);

      return false;
    }

    return true;
  }

  /**
   * Publishes the value to all observers of the given
   * in progress observables if observers exist.
   */
  private notifyInFlightObservers(key: string, value: any): void {
    if (this.inFlightObservables.has(key)) {
      const inFlight = this.inFlightObservables.get(key);
      const observersCount = inFlight.observers.length;

      if (observersCount) {
        inFlight.next(value);
      }

      inFlight.complete();

      this.inFlightObservables.delete(key);
    }
  }
}
